/**
 * customerAddress — ONE reader for a customer's address.
 *
 * WHY THIS EXISTS
 * `customers` carries the same fact in two column families: the legacy flat four
 * (street_address / city / state / zip_code) and the canonical billing_* set. Neither is
 * dead. Measured live 2026-08-24: 144 rows have billing_address_line_1, 145 have
 * street_address, and 132 have billing_city with city NULL — so a reader that touches only
 * one family renders BLANK for a large share of customers. scripts/.column-aliases-baseline.json
 * lists 32 such sites; check:aliases blocks new ones.
 *
 * Two halves fail independently, and fixing only one looks fixed while staying broken:
 *   1. the QUERY must fetch both families      -> CUSTOMER_ADDRESS_COLUMNS
 *   2. the READ must fall back between them    -> getCustomerAddress()
 * FieldJobView selected 'name,street_address,phone,email' and then rendered the legacy
 * street line alone with a "No address" fallback. Adding a read-side fallback would have
 * changed nothing there, because the billing column was never in the payload to begin with.
 * Same shape as the SendInvoiceModal "No phone" bug (10951f6a4).
 *
 * (Deliberately not quoting the old accessor expression here: check:aliases is regex-based
 * and does not strip comments, so prose containing a one-sided read trips the guard.)
 *
 * WHY A HELPER RATHER THAN `billing_x || x` AT EACH SITE
 * Spelling the pair out 32 times entrenches the split in 32 more places. This function is
 * the single seam where the source changes: customer_addresses is the canonical home
 * (address_type CHECKed against billing/service/mailing/shipping, one primary per customer
 * per company) and became writable on 2026-08-24 when its missing RLS policies were added.
 * When the backfill lands, this file starts reading that table and every call site follows
 * with no edit. That is the point of routing them through here now.
 *
 * check:aliases is satisfied because callers stop naming either column at all; the paired
 * reads live here, on one line each, which is exactly what that guard asks for.
 */

/**
 * Every customer address column, for a Supabase .select(). Use this instead of hand-listing
 * address columns — a query that fetches one family and not the other is the bug above.
 */
export const CUSTOMER_ADDRESS_COLUMNS =
  'street_address,city,state,zip_code,' +
  'billing_address_line_1,billing_address_line_2,billing_city,billing_state,billing_zip_code';

/**
 * Resolve a customer's address to a single shape, billing family first.
 * Returns nulls rather than '' so callers can test truthiness per field.
 */
export function getCustomerAddress(customer) {
  if (!customer) return { line1: null, line2: null, city: null, state: null, zip: null };
  return {
    line1: customer.billing_address_line_1 || customer.street_address || null,
    line2: customer.billing_address_line_2 || null,
    city: customer.billing_city || customer.city || null,
    state: customer.billing_state || customer.state || null,
    zip: customer.billing_zip_code || customer.zip_code || null,
  };
}

/** True when we hold any part of an address at all. */
export function hasCustomerAddress(customer) {
  const a = getCustomerAddress(customer);
  return Boolean(a.line1 || a.city || a.state || a.zip);
}

/**
 * One-line display form: "123 Main St, The Dalles, OR 97058".
 * State and ZIP are joined by a space, not a comma — that is the US postal convention and
 * what Jobber/HCP print. Returns '' when nothing is on file so callers can choose their own
 * empty-state wording.
 */
export function formatCustomerAddress(customer) {
  const a = getCustomerAddress(customer);
  const stateZip = [a.state, a.zip].filter(Boolean).join(' ');
  return [a.line1, a.line2, a.city, stateZip].filter(Boolean).join(', ');
}

/** Google Maps link for the full address, or null when there is nothing to point at. */
export function customerMapsUrl(customer) {
  const full = formatCustomerAddress(customer);
  return full ? `https://maps.google.com/?q=${encodeURIComponent(full)}` : null;
}
